import React, { FC, useMemo } from 'react'
import { Box, Typography, SxProps, useTheme } from '@mui/material'
import PatientGenderIcon from '../atoms/PatientGenderIcon'
import { PatientGender } from '@/shared/types/yakurekiCore'
import { getHeaderResponsiveStyles } from '@/shared/styles/responsive'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

interface PatientData {
  name?: string
  nameKana?: string
  gender?: PatientGender
  patientCode?: string
  lastVisitDate?: string
  age?: number | string
}

interface PatientInfoDisplayProps {
  patient: PatientData
  variant?: 'header' | 'drawer' | 'compact' | 'card'
  showIcon?: boolean
  showDetails?: boolean
  showNameKana?: boolean
  iconSize?: number
  sx?: SxProps
}

/**
 * 患者情報表示用共通コンポーネント
 * 複数の画面で統一された患者情報の表示を提供
 */
export const PatientInfoDisplay: FC<PatientInfoDisplayProps> = ({
  patient,
  variant = 'header',
  showIcon = true,
  showDetails = false,
  showNameKana = false,
  iconSize = 28,
  sx,
}) => {
  const theme = useTheme()

  // バリエーション別の設定
  const variantConfig = useMemo(() => {
    const configs = {
      header: {
        container: {
          display: 'flex',
          alignItems: 'center',
        },
        nameStyle: {
          variant: 'h6' as const,
          sx: getHeaderResponsiveStyles(theme).patientName,
        },
        defaultShowIcon: true,
        defaultShowDetails: false,
        defaultShowNameKana: false,
        nameSuffix: ' さん',
      },
      drawer: {
        container: {
          p: 2,
          backgroundColor: colorTokens.grey[100],
        },
        nameStyle: {
          variant: 'h6' as const,
        },
        defaultShowIcon: false,
        defaultShowDetails: true,
        defaultShowNameKana: true,
        nameSuffix: '',
      },
      compact: {
        container: {
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        },
        nameStyle: {
          variant: 'body2' as const,
        },
        defaultShowIcon: true,
        defaultShowDetails: false,
        defaultShowNameKana: false,
        nameSuffix: ' さん',
      },
      card: {
        container: {
          p: 1.5,
          border: `1px solid ${colorTokens.border.light}`,
          borderRadius: 1,
        },
        nameStyle: {
          variant: 'subtitle1' as const,
        },
        defaultShowIcon: true,
        defaultShowDetails: true,
        defaultShowNameKana: false,
        nameSuffix: ' さん',
      },
    }
    return configs[variant]
  }, [variant, theme])

  // 表示名の構成
  const displayName = useMemo(() => {
    if (!patient.name) return '患者情報なし'

    let name = patient.name

    // かな名の追加
    if (
      (showNameKana ?? variantConfig.defaultShowNameKana) &&
      patient.nameKana
    ) {
      name += ` (${patient.nameKana})`
    }

    // 敬語の追加
    name += variantConfig.nameSuffix

    // 年齢の追加
    if (patient.age != null) {
      name += ` (${patient.age}歳)`
    }

    return name
  }, [patient.name, patient.nameKana, patient.age, showNameKana, variantConfig])

  // 詳細情報の構成
  const detailsText = useMemo(() => {
    if (!(showDetails ?? variantConfig.defaultShowDetails)) return null

    const parts = []
    if (patient.patientCode) {
      parts.push(`患者ID: ${patient.patientCode}`)
    }
    if (patient.lastVisitDate) {
      parts.push(`前回来局日: ${patient.lastVisitDate}`)
    }

    return parts.length > 0 ? parts.join(' | ') : null
  }, [
    patient.patientCode,
    patient.lastVisitDate,
    showDetails,
    variantConfig.defaultShowDetails,
  ])

  const shouldShowIcon = showIcon ?? variantConfig.defaultShowIcon

  return (
    <Box sx={{ ...variantConfig.container, ...sx }}>
      {/* 性別アイコン */}
      {shouldShowIcon && (
        <PatientGenderIcon
          gender={patient.gender || 'male'}
          sx={{ fontSize: iconSize, mr: 1 }}
        />
      )}
      {/* 患者情報 */}
      <Box
        sx={[
          variant === 'drawer'
            ? {
                flex: 1,
              }
            : {
                flex: 'none',
              },
        ]}
      >
        {/* 患者名 */}
        <Typography {...variantConfig.nameStyle}>{displayName}</Typography>
        {/* 詳細情報 */}
        {detailsText && (
          <Typography variant="body2" color="text.secondary">
            {detailsText}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
